import React, { useState, useEffect, useCallback, useMemo } from 'react';
import apiClient from '../services/api';
import { PageHeader, PageDescription } from '../main/LayoutStyles';
import { Card, AlertBox } from '../main/CommonStyles';
import { Loader2, AlertTriangle } from 'lucide-react';
import styled from 'styled-components';

const SettingsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem; /* Zmniejszony odstęp */
`;

const SettingItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 1rem; /* Zmniejszony padding pionowy */
    background-color: #fff;
    border: 1px solid ${({ theme }) => theme.colors.borderColor};
    border-radius: 8px;
    transition: background-color 0.2s;
    opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};

    &:hover {
        background-color: ${({ theme, disabled }) => !disabled && theme.colors.neutralLight};
    }

    span {
        font-weight: 500;
        font-size: 0.9rem; /* Mniejsza czcionka */
    }
`;

const RoleSelector = styled.div`
    margin-bottom: 1rem; /* Zmniejszony margines */
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    label {
        font-weight: 600;
        font-size: 0.9rem;
        color: ${({ theme }) => theme.colors.textMedium};
    }

    select {
        padding: 0.75rem;
        border-radius: 8px;
        border: 1px solid #ccc;
        background-color: #fff;
        font-size: 1rem;
    }
`;

const ToggleSwitch = styled.label`
    position: relative;
    display: inline-block;
    width: 44px;
    height: 24px;
    cursor: pointer;

    input {
        opacity: 0;
        width: 0;
        height: 0;
    }

    .slider {
        position: absolute;
        top: 0; left: 0; right: 0; bottom: 0;
        background-color: #ccc;
        transition: .4s;
        border-radius: 34px;
    }

    .slider:before {
        position: absolute;
        content: "";
        height: 16px;
        width: 16px;
        left: 4px;
        bottom: 4px;
        background-color: white;
        transition: .4s;
        border-radius: 50%;
    }

    input:checked + .slider {
        background-color: ${({ theme }) => theme.colors.primary};
    }

    input:disabled + .slider {
        cursor: not-allowed;
        background-color: #e0e0e0;
    }

    input:checked + .slider:before {
        transform: translateX(20px);
    }
`;

const featureDisplayMap = {
    AI_CHAT: { name: 'Czat AI' }, JOURNAL: { name: 'Dziennik' },
    EXERCISES: { name: 'Ćwiczenia' }, TESTS: { name: 'Testy' },
    KNOWLEDGE_BASE: { name: 'Baza Wiedzy' }, ARTICLE_CREATION: { name: 'Tworzenie Artykułów' },
    APPOINTMENTS: { name: 'Wizyty' }, PSYCHOLOGIST_LIST: { name: 'Lista Specjalistów' },
    REPORTING: { name: 'Zgłaszanie Spraw' }, HELP: { name: 'Pomoc' },
    ADMIN_PANEL: { name: 'Panel Admina Organizacji' }, SPECIALIST_PANEL: { name: 'Panel Specjalisty' }
};

const ROLES_TO_MANAGE = ["ROLE_USER", "ROLE_ADMIN", "ROLE_PSYCHOLOGIST", "ROLE_CHILD", "ROLE_PARENT", "ROLE_SCHOOL_STAFF", "ROLE_GUEST"];

const RolePermissionManagement = () => {
    const [systemToggles, setSystemToggles] = useState([]);
    const [roleToggles, setRoleToggles] = useState([]);
    const [status, setStatus] = useState('loading');
    const [error, setError] = useState('');
    const [selectedRole, setSelectedRole] = useState(ROLES_TO_MANAGE[0]);

    const fetchData = useCallback(async () => {
        setStatus('loading');
        try {
            const [systemRes, roleRes] = await Promise.all([
                apiClient.get('/features/system-admin/system-toggles'),
                apiClient.get('/features/system-admin/role-toggles')
            ]);
            setSystemToggles(systemRes.data);
            setRoleToggles(roleRes.data);
            setStatus('success');
        } catch (err) {
            setError("Nie udało się załadować konfiguracji uprawnień.");
            setStatus('error');
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleRoleToggleChange = async (featureName, role) => {
        const currentToggle = roleToggles.find(t => t.featureName === featureName && t.role === role);
        const currentEnabledState = currentToggle ? currentToggle.enabled : false;

        setStatus('loading');
        try {
            await apiClient.put('/features/system-admin/role-toggles', { featureName, role, enabled: !currentEnabledState });
            await fetchData();
        } catch (err) {
            setError("Błąd podczas aktualizacji przypisania roli.");
            setStatus('error');
        }
    };

    const systemTogglesMap = useMemo(() => {
        return new Map(systemToggles.map(t => [t.featureName, t.enabled]));
    }, [systemToggles]);

    const roleTogglesForSelectedRole = useMemo(() => {
        const toggles = new Map();
        roleToggles
            .filter(t => t.role === selectedRole)
            .forEach(t => toggles.set(t.featureName, t.enabled));
        return toggles;
    }, [roleToggles, selectedRole]);

    if (status === 'loading' && systemToggles.length === 0) {
        return <div style={{textAlign: 'center', padding: '2rem'}}><Loader2 className="icon-spin" size={32} /></div>;
    }

    return (
        <div>
            <PageHeader>Uprawnienia dla Ról</PageHeader>
            <PageDescription>Wybierz rolę z listy, a następnie włącz lub wyłącz dla niej dostęp do poszczególnych funkcji. Funkcje wyłączone globalnie nie mogą być tutaj włączone.</PageDescription>
            {error && <AlertBox type="error" style={{marginBottom: '1rem'}}>{error}</AlertBox>}
            <Card>
                <RoleSelector>
                    <label htmlFor="role-select">Wybierz rolę do edycji:</label>
                    <select id="role-select" value={selectedRole} onChange={e => setSelectedRole(e.target.value)}>
                        {ROLES_TO_MANAGE.map(role => (
                            <option key={role} value={role}>{role.replace('ROLE_', '').replace('_', ' ')}</option>
                        ))}
                    </select>
                </RoleSelector>

                <SettingsList>
                    {Object.entries(featureDisplayMap).map(([featureName, { name }]) => {
                        const isSystemEnabled = systemTogglesMap.get(featureName) ?? false;
                        const isRoleEnabled = roleTogglesForSelectedRole.get(featureName) ?? false;

                        return (
                            <SettingItem key={`${selectedRole}-${featureName}`} disabled={!isSystemEnabled}>
                                <span>{name}</span>
                                <ToggleSwitch title={isSystemEnabled ? `Kliknij, aby przełączyć dla ${selectedRole}` : 'Funkcja jest wyłączona w całym systemie'}>
                                    <input
                                        type="checkbox"
                                        checked={isSystemEnabled && isRoleEnabled}
                                        onChange={() => handleRoleToggleChange(featureName, selectedRole)}
                                        disabled={!isSystemEnabled || status === 'loading'}
                                    />
                                    <span className="slider"></span>
                                </ToggleSwitch>
                            </SettingItem>
                        );
                    })}
                </SettingsList>
            </Card>
        </div>
    );
};

export default RolePermissionManagement;